import React from "react";
import { AlertTriangle } from "lucide-react";
import { ProductColumnHeader } from "src/enums";
import { POProductConfirmationInputField } from "src/types";
import { DateInputBox, TextInputBox } from "src/components/ui/inputs";
import { Table } from "src/components";
import { useAuthorisation } from "src/context";
import { RoleHelper } from "src/helpers";
import { formatFloatWithTwoDecimals } from "src/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "src/shadcn/ui/tooltip";

interface OrderProductConfirmationInputTableProps {
  orderProductConfirmations: POProductConfirmationInputField[];
  setOrderProductConfirmations: React.Dispatch<React.SetStateAction<POProductConfirmationInputField[]>>;
  vendorLocation?: string;
}

const OrderProductConfirmationInputTable: React.FC<OrderProductConfirmationInputTableProps> = ({
  orderProductConfirmations,
  setOrderProductConfirmations,
  vendorLocation
}) => {
  const { userRole } = useAuthorisation();
  const calculateFulfillmentRate = (confirmedQty: string, orderQty: string): number => {
    const confirmed = parseFloat(confirmedQty) || 0;
    const ordered = parseFloat(orderQty) || 0;
    if (ordered === 0) return 100;
    return (confirmed / ordered) * 100;
  };

  const shouldShowRestockDate = (confirmedQty: string, orderQty: string): boolean => {
    const fulfillmentRate = calculateFulfillmentRate(confirmedQty, orderQty);
    return fulfillmentRate < 50;
  };

  const handleProductConfirmationsInputChange = (
    id: string,
    fieldName: keyof POProductConfirmationInputField,
    value: string | boolean
  ) => {
    setOrderProductConfirmations(
      orderProductConfirmations.map((productConfirmation) => {
        if (productConfirmation.fieldId === id) {
          const isConfirmedQtyChange = fieldName === "confirmedQty";
          const isDiscontinuedChange = fieldName === "isDiscontinued";

          // Calculate if restock date should be shown after the change
          const newConfirmedQty = isConfirmedQtyChange ? (value as string) : productConfirmation.confirmedQty;
          const shouldShowRestock = shouldShowRestockDate(newConfirmedQty, productConfirmation.orderQty);
          const currentlyShowsRestock = shouldShowRestockDate(
            productConfirmation.confirmedQty,
            productConfirmation.orderQty
          );

          return {
            ...productConfirmation,
            [fieldName]: value,
            // Clear restock date if fulfillment rate goes from < 50% to >= 50%
            ...(isConfirmedQtyChange &&
              currentlyShowsRestock &&
              !shouldShowRestock && {
                estRestockDate: "",
                isDiscontinued: false
              }),
            // Clear restock date if marked as discontinued
            ...(isDiscontinuedChange && value === true && { estRestockDate: "" })
          };
        }
        return productConfirmation;
      })
    );
  };

  // const isUSVendor = vendorLocation === "United States";
  const shouldShowUnitCost = RoleHelper.isHealfOperationsTeam(userRole) || userRole === "HEALF_ACCOUNTANT";
  // (userRole === "VENDOR" && isUSVendor);

  const unitCostColumn = shouldShowUnitCost
    ? [
        {
          header: (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2 cursor-help">
                    <span>{ProductColumnHeader.UNIT_COST}</span>
                    <AlertTriangle className="h-4 w-4 text-warning-500" aria-hidden="true" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <div className="space-y-1">
                    <p className="font-semibold text-warning-200">Exchange Rate Disclaimer</p>
                    <p className="text-xs text-gray-300 leading-relaxed">
                      Prices are displayed in British Pounds (GBP). Due to constantly fluctuating exchange rates, the
                      displayed amounts may not reflect the exact current value in US Dollars.
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ),
          column: (row: POProductConfirmationInputField) =>
            row.unitCost !== undefined ? (
              <span className="font-medium text-secondary-900">£{formatFloatWithTwoDecimals(row.unitCost)}</span>
            ) : (
              <span className="text-secondary-500">-</span>
            ),
          styles: { minWidth: "110px", textAlign: "center" as const }
        }
      ]
    : [];

  const productConfirmationTableColumns = [
    {
      header: ProductColumnHeader.NAME,
      column: (row: POProductConfirmationInputField) => <div>{row.productName}</div>,
      styles: { minWidth: "200px" }
    },
    {
      header: ProductColumnHeader.SIZE,
      column: (row: POProductConfirmationInputField) => <div>{row.productSize}</div>
    },
    {
      header: ProductColumnHeader.BARCODE,
      column: (row: POProductConfirmationInputField) => <div>{row.productBarcode}</div>
    },
    ...(orderProductConfirmations?.some((orderProductConfirmation) => orderProductConfirmation.productExtraInfo)
      ? [
          {
            header: ProductColumnHeader.EXTRA_INFO,
            column: (row: POProductConfirmationInputField) => row.productExtraInfo
          }
        ]
      : []),
    {
      header: ProductColumnHeader.ORDER_QUANTITY,
      column: (row: POProductConfirmationInputField) => <div>{row.orderQty}</div>,
      styles: { minWidth: "120px", textAlign: "center" as const }
    },
    ...unitCostColumn,
    {
      header: ProductColumnHeader.CONFIRMED_QTY,
      column: (row: POProductConfirmationInputField) => {
        const confirmedQty =
          orderProductConfirmations
            .find((orderProductConfirmation) => orderProductConfirmation.fieldId === row.fieldId)
            ?.confirmedQty?.toString() || "";
        return (
          <div className="w-20">
            <TextInputBox
              id={`confirmed-quantity-${row.fieldId}`}
              value={confirmedQty}
              onChange={(value) => handleProductConfirmationsInputChange(row.fieldId, "confirmedQty", value)}
              maxLength={40}
              placeholder={`/ ${row.orderQty}`}
              size="sm"
            />
          </div>
        );
      }
    },
    ...(orderProductConfirmations.some((orderProductConfirmation) =>
      shouldShowRestockDate(orderProductConfirmation.confirmedQty, orderProductConfirmation.orderQty)
    )
      ? [
          {
            header: ProductColumnHeader.EST_RESTOCK_DATE,
            column: (row: POProductConfirmationInputField) => {
              const productConfirmation = orderProductConfirmations.find(
                (orderProductConfirmation) => orderProductConfirmation.fieldId === row.fieldId
              );
              const estRestockDate = productConfirmation?.estRestockDate || "";
              const confirmedQty = productConfirmation?.confirmedQty?.toString() || "";
              const isDiscontinued = productConfirmation?.isDiscontinued || false;
              const shouldShowRestock = shouldShowRestockDate(confirmedQty, row.orderQty);

              return shouldShowRestock ? (
                <div>
                  <DateInputBox
                    id={`restock-date-${row.fieldId}`}
                    value={estRestockDate}
                    onChange={(value: string) =>
                      handleProductConfirmationsInputChange(row.fieldId, "estRestockDate", value)
                    }
                    disabled={isDiscontinued}
                  />
                  {!estRestockDate && !isDiscontinued && (
                    <div className="text-xs text-error-600 mt-1">Required when fulfillment is less than 50%</div>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="checkbox"
                      id={`discontinued-${row.fieldId}`}
                      checked={isDiscontinued}
                      onChange={(e) =>
                        handleProductConfirmationsInputChange(row.fieldId, "isDiscontinued", e.target.checked)
                      }
                      className="w-4 h-4 text-primary-600 bg-white border-secondary-300 rounded focus:ring-primary-500 focus:ring-2 cursor-pointer"
                    />
                    <label
                      htmlFor={`discontinued-${row.fieldId}`}
                      className="text-sm text-secondary-700 cursor-pointer"
                    >
                      Discontinued
                    </label>
                  </div>
                </div>
              ) : (
                ""
              );
            }
          }
        ]
      : [])
  ];

  return <Table columns={productConfirmationTableColumns} data={orderProductConfirmations} />;
};

export { OrderProductConfirmationInputTable };
